<template>
  <main>
    <v-container>
      <v-row>
        <v-col>
          <v-form ref="form" v-model="valid" class="py-4 px-4" lazy-validation>
            <v-card outlined>
              <!-- Tabs -->
              <v-tabs v-model="tab" background-color="primary" dark show-arrows>
                <v-tab>Details</v-tab>
                <v-tab>Dates</v-tab>
                <v-tab>Additional Details</v-tab>
                <v-tab>Links</v-tab>
              </v-tabs>

              <!-- Tab Items -->
              <v-tabs-items v-model="tab">
                <v-tab-item :transition="false" :reverse-transition="false" class="py-4 px-4">
                  <v-text-field v-model="formData.name" label="Name" :required="true" :rules="rules.name" />
                  <v-text-field
                    v-model="formData.symbol"
                    label="Symbol"
                    :required="true"
                    :rules="rules.symbol"
                    class="mb-1"
                  />
                  <v-select-image v-model="formData.logo" :size="250" title="Logo" label="Logo" show-extra-previews />
                  <v-text-field
                    v-model="formData.blockchain_type"
                    label="Blockchain Type"
                    :required="true"
                    :rules="rules.blockchain_type"
                  />
                  <v-text-field v-model="formData.platform" label="Platform" :required="true" :rules="rules.platform" />
                  <v-card outlined class="mb-4">
                    <v-tabs v-model="tabs.short_description" show-arrows>
                      <v-tab v-for="item in Object.keys(formData.short_description)" :key="item">{{ item }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="tabs.short_description">
                      <v-tab-item
                        v-for="item in Object.keys(formData.short_description)"
                        :key="item"
                        :transition="false"
                        :reverse-transition="false"
                        class="px-4 py-4"
                      >
                        <v-textarea
                          v-model="formData.short_description[item]"
                          :label="`Short Desciption (${item})`"
                          :required="!!(item == 'en')"
                          :rules="item == 'en' ? rules.short_description : [v => true]"
                        />
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                  <v-card outlined class="mb-4">
                    <v-tabs v-model="tabs.description" show-arrows>
                      <v-tab v-for="item in Object.keys(formData.description)" :key="item">{{ item }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="tabs.description">
                      <v-tab-item
                        v-for="item in Object.keys(formData.description)"
                        :key="item"
                        :transition="false"
                        :reverse-transition="false"
                        class="px-4 py-4"
                      >
                        <v-textarea
                          v-model="formData.description[item]"
                          :label="`Description (${item})`"
                          :required="!!(item == 'en')"
                          :rules="item == 'en' ? rules.description : [v => true]"
                        />
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>

                  <v-select
                    v-model="formData.country"
                    :items="countries"
                    :rules="rules.country"
                    label="Country"
                    item-text="name"
                    item-value="code"
                    required
                  />
                </v-tab-item>
                <v-tab-item :transition="false" :reverse-transition="false" class="py-4 px-4">
                  <datetime-picker
                    :datetime="parseISO(formData.pre_ico_opening_date)"
                    label="Pre Ico Opening Date"
                    clearable
                    @input="onInputSetDate($event, 'pre_ico_opening_date')"
                  />
                  <datetime-picker
                    :datetime="parseISO(formData.pre_ico_closing_date)"
                    label="Pre Ico Closing Date"
                    clearable
                    @input="onInputSetDate($event, 'pre_ico_closing_date')"
                  />
                  <datetime-picker
                    :datetime="parseISO(formData.ico_opening_date)"
                    label="Ico Opening Date"
                    clearable
                    @input="onInputSetDate($event, 'ico_opening_date')"
                  />
                  <datetime-picker
                    :datetime="parseISO(formData.ico_closing_date)"
                    label="Ico Closing Date"
                    clearable
                    @input="onInputSetDate($event, 'ico_closing_date')"
                  />
                </v-tab-item>
                <v-tab-item :transition="false" :reverse-transition="false" class="py-4 px-4">
                  <v-card outlined class="mb-4">
                    <v-tabs v-model="tabs.team" show-arrows>
                      <v-tab v-for="item in Object.keys(formData.team)" :key="item">{{ item }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="tabs.team">
                      <v-tab-item
                        v-for="item in Object.keys(formData.team)"
                        :key="item"
                        :transition="false"
                        :reverse-transition="false"
                        class="px-4 py-4"
                      >
                        <froala-input
                          v-model="formData.team[item]"
                          :label="`Team (${item})`"
                          :required="!!(item == 'en')"
                          :rules="item == 'en' ? rules.team : [v => true]"
                        />
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                  <v-card outlined class="mb-4">
                    <v-tabs v-model="tabs.advisors" show-arrows>
                      <v-tab v-for="item in Object.keys(formData.advisors)" :key="item">{{ item }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="tabs.advisors">
                      <v-tab-item
                        v-for="item in Object.keys(formData.advisors)"
                        :key="item"
                        :transition="false"
                        :reverse-transition="false"
                        class="px-4 py-4"
                      >
                        <froala-input
                          v-model="formData.advisors[item]"
                          :label="`Advisors (${item})`"
                          :required="!!(item == 'en')"
                          :rules="item == 'en' ? rules.advisors : [v => true]"
                        />
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-tab-item>
                <v-tab-item :transition="false" :reverse-transition="false" class="py-4 px-4">
                  <v-text-field v-model="formData.website" label="Website" />
                  <v-text-field v-model="formData.blog" label="Blog" />
                  <v-text-field v-model="formData.facebook" label="Facebook" />
                  <v-text-field v-model="formData.twitter" label="Twitter" />
                  <v-text-field v-model="formData.linkedin" label="Linkedin" />
                  <v-text-field v-model="formData.telegram" label="Telegram" />
                  <v-text-field v-model="formData.slack" label="Slack" />
                </v-tab-item>
              </v-tabs-items>
            </v-card>
          </v-form>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col class="d-flex items-center justify-end">
          <v-btn :loading="loading.cancelling" :disabled="disabled" class="mr-4" text @click="cancelCreatingResource">
            Cancel
          </v-btn>
          <v-btn
            :loading="loading.creatingAlt"
            :disabled="disabled"
            class="mr-4"
            color="primary"
            @click="createResourceWithoutRedirect"
          >
            Create & Add Another
          </v-btn>
          <v-btn :loading="loading.creating" :disabled="disabled" color="primary" @click="createResourceWithRedirect">
            Create {{ resourceType }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </main>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { format, parseISO, formatISO } from 'date-fns'

import PermissionsHandler from '@/mixins/permissions-handler'
import CreateResource from '@/mixins/create-resource'
import DatetimePicker from '@/components/datetime-picker'
import FroalaInput from '@/components/forms/froala-input'
import VSelectImage from '@/components/forms/select-image'

export default {
  name: 'IcosCreate',
  components: { DatetimePicker, FroalaInput, VSelectImage },
  mixins: [PermissionsHandler, CreateResource],
  data() {
    return {
      resourceType: 'Ico',
      resourcePrefix: 'content.icos',
      rules: {
        name: [v => !!v || 'Name is required', v => (!!v && v.length > 0) || 'Name must be greater than 0'],
        symbol: [v => !!v || 'Symbol is required', v => (!!v && v.length > 0) || 'Symbol must be greater than 0'],
        blockchain_type: [
          v => !!v || 'Block Chain Type is required',
          v => (!!v && v.length > 0) || 'Block Chain Type must be greater than 0',
        ],
        platform: [v => !!v || 'Platform is required', v => (!!v && v.length > 0) || 'Platform must be greater than 0'],
        short_description: [
          v => !!v || 'Short Description is required',
          v => (!!v && v.length > 0) || 'Short Description must be greater than 0',
        ],
        country: [v => !!v || 'Country is required', v => (!!v && v.length > 0) || 'Country must be greater than 0'],
      },
      tabs: {
        description: null,
        short_description: null,
        team: null,
        advisors: null,
      },
      formData: {
        name: null,
        symbol: null,
        description: { en: null, de: null, es: null, fr: null, it: null, nl: null, pt: null, sw: null },
        short_description: { en: null, de: null, es: null, fr: null, it: null, nl: null, pt: null, sw: null },
        logo: null,
        blockchain_type: null,
        platform: null,
        pre_ico_opening_date: null,
        pre_ico_closing_date: null,
        ico_opening_date: null,
        ico_closing_date: null,
        country: null,
        website: null,
        team: { en: null, de: null, es: null, fr: null, it: null, nl: null, pt: null, sw: null },
        advisors: { en: null, de: null, es: null, fr: null, it: null, nl: null, pt: null, sw: null },
        blog: null,
        facebook: null,
        twitter: null,
        linkedin: null,
        telegram: null,
        slack: null,
      },
      countries: [
        {
          code: 'AF',
          name: 'Afghanistan',
        },
        {
          code: 'AX',
          name: 'Åland Islands',
        },
        {
          code: 'AL',
          name: 'Albania',
        },
        {
          code: 'DZ',
          name: 'Algeria',
        },
        {
          code: 'AS',
          name: 'American Samoa',
        },
        {
          code: 'AD',
          name: 'Andorra',
        },
        {
          code: 'AO',
          name: 'Angola',
        },
        {
          code: 'AI',
          name: 'Anguilla',
        },
        {
          code: 'AQ',
          name: 'Antarctica',
        },
        {
          code: 'AG',
          name: 'Antigua and Barbuda',
        },
        {
          code: 'AR',
          name: 'Argentina',
        },
        {
          code: 'AM',
          name: 'Armenia',
        },
        {
          code: 'AW',
          name: 'Aruba',
        },
        {
          code: 'AU',
          name: 'Australia',
        },
        {
          code: 'AT',
          name: 'Austria',
        },
        {
          code: 'AZ',
          name: 'Azerbaijan',
        },
        {
          code: 'BS',
          name: 'Bahamas',
        },
        {
          code: 'BH',
          name: 'Bahrain',
        },
        {
          code: 'BD',
          name: 'Bangladesh',
        },
        {
          code: 'BB',
          name: 'Barbados',
        },
        {
          code: 'BY',
          name: 'Belarus',
        },
        {
          code: 'BE',
          name: 'Belgium',
        },
        {
          code: 'BZ',
          name: 'Belize',
        },
        {
          code: 'BJ',
          name: 'Benin',
        },
        {
          code: 'BM',
          name: 'Bermuda',
        },
        {
          code: 'BT',
          name: 'Bhutan',
        },
        {
          code: 'BO',
          name: 'Bolivia',
        },
        {
          code: 'BQ',
          name: 'Bonaire, Sint Eustatius and Saba',
        },
        {
          code: 'BA',
          name: 'Bosnia and Herzegovina',
        },
        {
          code: 'BW',
          name: 'Botswana',
        },
        {
          code: 'BV',
          name: 'Bouvet Island',
        },
        {
          code: 'BR',
          name: 'Brazil',
        },
        {
          code: 'IO',
          name: 'British Indian Ocean Territory',
        },
        {
          code: 'BN',
          name: 'Brunei',
        },
        {
          code: 'BG',
          name: 'Bulgaria',
        },
        {
          code: 'BF',
          name: 'Burkina Faso',
        },
        {
          code: 'BI',
          name: 'Burundi',
        },
        {
          code: 'KH',
          name: 'Cambodia',
        },
        {
          code: 'CM',
          name: 'Cameroon',
        },
        {
          code: 'CA',
          name: 'Canada',
        },
        {
          code: 'CV',
          name: 'Cape Verde',
        },
        {
          code: 'KY',
          name: 'Cayman Islands',
        },
        {
          code: 'CF',
          name: 'Central African Republic',
        },
        {
          code: 'TD',
          name: 'Chad',
        },
        {
          code: 'CL',
          name: 'Chile',
        },
        {
          code: 'CN',
          name: 'China',
        },
        {
          code: 'CX',
          name: 'Christmas Island',
        },
        {
          code: 'CC',
          name: 'Cocos (Keeling) Islands',
        },
        {
          code: 'CO',
          name: 'Colombia',
        },
        {
          code: 'KM',
          name: 'Comoros',
        },
        {
          code: 'CG',
          name: 'Congo',
        },
        {
          code: 'CD',
          name: 'Congo, Democratic Republic',
        },
        {
          code: 'CK',
          name: 'Cook Islands',
        },
        {
          code: 'CR',
          name: 'Costa Rica',
        },
        {
          code: 'CI',
          name: "Côte d'Ivoire",
        },
        {
          code: 'HR',
          name: 'Croatia',
        },
        {
          code: 'CU',
          name: 'Cuba',
        },
        {
          code: 'CW',
          name: 'Curaçao',
        },
        {
          code: 'CY',
          name: 'Cyprus',
        },
        {
          code: 'CZ',
          name: 'Czechia',
        },
        {
          code: 'DK',
          name: 'Denmark',
        },
        {
          code: 'DJ',
          name: 'Djibouti',
        },
        {
          code: 'DM',
          name: 'Dominica',
        },
        {
          code: 'DO',
          name: 'Dominican Republic',
        },
        {
          code: 'EC',
          name: 'Ecuador',
        },
        {
          code: 'EG',
          name: 'Egypt',
        },
        {
          code: 'SV',
          name: 'El Salvador',
        },
        {
          code: 'GQ',
          name: 'Equatorial Guinea',
        },
        {
          code: 'ER',
          name: 'Eritrea',
        },
        {
          code: 'EE',
          name: 'Estonia',
        },
        {
          code: 'ET',
          name: 'Ethiopia',
        },
        {
          code: 'FK',
          name: 'Falkland Islands (Malvinas)',
        },
        {
          code: 'FO',
          name: 'Faroe Islands',
        },
        {
          code: 'FJ',
          name: 'Fiji',
        },
        {
          code: 'FI',
          name: 'Finland',
        },
        {
          code: 'FR',
          name: 'France',
        },
        {
          code: 'GF',
          name: 'French Guiana',
        },
        {
          code: 'PF',
          name: 'French Polynesia',
        },
        {
          code: 'TF',
          name: 'French Southern Territories',
        },
        {
          code: 'GA',
          name: 'Gabon',
        },
        {
          code: 'GM',
          name: 'Gambia',
        },
        {
          code: 'GE',
          name: 'Georgia',
        },
        {
          code: 'DE',
          name: 'Germany',
        },
        {
          code: 'GH',
          name: 'Ghana',
        },
        {
          code: 'GI',
          name: 'Gibraltar',
        },
        {
          code: 'GR',
          name: 'Greece',
        },
        {
          code: 'GL',
          name: 'Greenland',
        },
        {
          code: 'GD',
          name: 'Grenada',
        },
        {
          code: 'GP',
          name: 'Guadeloupe',
        },
        {
          code: 'GU',
          name: 'Guam',
        },
        {
          code: 'GT',
          name: 'Guatemala',
        },
        {
          code: 'GG',
          name: 'Guernsey',
        },
        {
          code: 'GN',
          name: 'Guinea',
        },
        {
          code: 'GW',
          name: 'Guinea-Bissau',
        },
        {
          code: 'GY',
          name: 'Guyana',
        },
        {
          code: 'HT',
          name: 'Haiti',
        },
        {
          code: 'HM',
          name: 'Heard Island and Mcdonald Islands',
        },
        {
          code: 'VA',
          name: 'Vatican City',
        },
        {
          code: 'HN',
          name: 'Honduras',
        },
        {
          code: 'HK',
          name: 'Hong Kong',
        },
        {
          code: 'HU',
          name: 'Hungary',
        },
        {
          code: 'IS',
          name: 'Iceland',
        },
        {
          code: 'IN',
          name: 'India',
        },
        {
          code: 'ID',
          name: 'Indonesia',
        },
        {
          code: 'IR',
          name: 'Iran',
        },
        {
          code: 'IQ',
          name: 'Iraq',
        },
        {
          code: 'IE',
          name: 'Ireland',
        },
        {
          code: 'IM',
          name: 'Isle of Man',
        },
        {
          code: 'IL',
          name: 'Israel',
        },
        {
          code: 'IT',
          name: 'Italy',
        },
        {
          code: 'JM',
          name: 'Jamaica',
        },
        {
          code: 'JP',
          name: 'Japan',
        },
        {
          code: 'JE',
          name: 'Jersey',
        },
        {
          code: 'JO',
          name: 'Jordan',
        },
        {
          code: 'KZ',
          name: 'Kazakhstan',
        },
        {
          code: 'KE',
          name: 'Kenya',
        },
        {
          code: 'KI',
          name: 'Kiribati',
        },
        {
          code: 'KP',
          name: "Korea, Democratic People's Republic Of",
        },
        {
          code: 'KR',
          name: 'South Korea',
        },
        {
          code: 'XK',
          name: 'Kosovo',
        },
        {
          code: 'KW',
          name: 'Kuwait',
        },
        {
          code: 'KG',
          name: 'Kyrgyzstan',
        },
        {
          code: 'LA',
          name: 'Laos',
        },
        {
          code: 'LV',
          name: 'Latvia',
        },
        {
          code: 'LB',
          name: 'Lebanon',
        },
        {
          code: 'LS',
          name: 'Lesotho',
        },
        {
          code: 'LR',
          name: 'Liberia',
        },
        {
          code: 'LY',
          name: 'Libya',
        },
        {
          code: 'LI',
          name: 'Liechtenstein',
        },
        {
          code: 'LT',
          name: 'Lithuania',
        },
        {
          code: 'LU',
          name: 'Luxembourg',
        },
        {
          code: 'MO',
          name: 'Macao',
        },
        {
          code: 'MK',
          name: 'North Macedonia',
        },
        {
          code: 'MG',
          name: 'Madagascar',
        },
        {
          code: 'MW',
          name: 'Malawi',
        },
        {
          code: 'MY',
          name: 'Malaysia',
        },
        {
          code: 'MV',
          name: 'Maldives',
        },
        {
          code: 'ML',
          name: 'Mali',
        },
        {
          code: 'MT',
          name: 'Malta',
        },
        {
          code: 'MH',
          name: 'Marshall Islands',
        },
        {
          code: 'MQ',
          name: 'Martinique',
        },
        {
          code: 'MR',
          name: 'Mauritania',
        },
        {
          code: 'MU',
          name: 'Mauritius',
        },
        {
          code: 'YT',
          name: 'Mayotte',
        },
        {
          code: 'MX',
          name: 'Mexico',
        },
        {
          code: 'FM',
          name: 'Micronesia',
        },
        {
          code: 'MD',
          name: 'Moldova',
        },
        {
          code: 'MC',
          name: 'Monaco',
        },
        {
          code: 'MN',
          name: 'Mongolia',
        },
        {
          code: 'ME',
          name: 'Montenegro',
        },
        {
          code: 'MS',
          name: 'Montserrat',
        },
        {
          code: 'MA',
          name: 'Morocco',
        },
        {
          code: 'MZ',
          name: 'Mozambique',
        },
        {
          code: 'MM',
          name: 'Myanmar',
        },
        {
          code: 'NA',
          name: 'Namibia',
        },
        {
          code: 'NR',
          name: 'Nauru',
        },
        {
          code: 'NP',
          name: 'Nepal',
        },
        {
          code: 'NL',
          name: 'Netherlands',
        },
        {
          code: 'NC',
          name: 'New Caledonia',
        },
        {
          code: 'NZ',
          name: 'New Zealand',
        },
        {
          code: 'NI',
          name: 'Nicaragua',
        },
        {
          code: 'NE',
          name: 'Niger',
        },
        {
          code: 'NG',
          name: 'Nigeria',
        },
        {
          code: 'NU',
          name: 'Niue',
        },
        {
          code: 'NF',
          name: 'Norfolk Island',
        },
        {
          code: 'MP',
          name: 'Northern Mariana Islands',
        },
        {
          code: 'NO',
          name: 'Norway',
        },
        {
          code: 'OM',
          name: 'Oman',
        },
        {
          code: 'PK',
          name: 'Pakistan',
        },
        {
          code: 'PW',
          name: 'Palau',
        },
        {
          code: 'PS',
          name: 'Palestinian Territories',
        },
        {
          code: 'PA',
          name: 'Panama',
        },
        {
          code: 'PG',
          name: 'Papua New Guinea',
        },
        {
          code: 'PY',
          name: 'Paraguay',
        },
        {
          code: 'PE',
          name: 'Peru',
        },
        {
          code: 'PH',
          name: 'Philippines',
        },
        {
          code: 'PN',
          name: 'Pitcairn Islands',
        },
        {
          code: 'PL',
          name: 'Poland',
        },
        {
          code: 'PT',
          name: 'Portugal',
        },
        {
          code: 'PR',
          name: 'Puerto Rico',
        },
        {
          code: 'QA',
          name: 'Qatar',
        },
        {
          code: 'RE',
          name: 'Réunion',
        },
        {
          code: 'RO',
          name: 'Romania',
        },
        {
          code: 'RU',
          name: 'Russia',
        },
        {
          code: 'RW',
          name: 'Rwanda',
        },
        {
          code: 'BL',
          name: 'St. Barthélemy',
        },
        {
          code: 'SH',
          name: 'St. Helena',
        },
        {
          code: 'KN',
          name: 'Saint Kitts and Nevis',
        },
        {
          code: 'LC',
          name: 'St. Lucia',
        },
        {
          code: 'MF',
          name: 'St. Martin',
        },
        {
          code: 'PM',
          name: 'Saint Pierre and Miquelon',
        },
        {
          code: 'VC',
          name: 'Saint Vincent and Grenadines',
        },
        {
          code: 'WS',
          name: 'Samoa',
        },
        {
          code: 'SM',
          name: 'San Marino',
        },
        {
          code: 'ST',
          name: 'Sao Tome and Principe',
        },
        {
          code: 'SA',
          name: 'Saudi Arabia',
        },
        {
          code: 'SN',
          name: 'Senegal',
        },
        {
          code: 'RS',
          name: 'Serbia',
        },
        {
          code: 'SC',
          name: 'Seychelles',
        },
        {
          code: 'SL',
          name: 'Sierra Leone',
        },
        {
          code: 'SG',
          name: 'Singapore',
        },
        {
          code: 'SX',
          name: 'Sint Maarten',
        },
        {
          code: 'SK',
          name: 'Slovakia',
        },
        {
          code: 'SI',
          name: 'Slovenia',
        },
        {
          code: 'SB',
          name: 'Solomon Islands',
        },
        {
          code: 'SO',
          name: 'Somalia',
        },
        {
          code: 'ZA',
          name: 'South Africa',
        },
        {
          code: 'GS',
          name: 'South Georgia and Sandwich Isl.',
        },
        {
          code: 'SS',
          name: 'South Sudan',
        },
        {
          code: 'ES',
          name: 'Spain',
        },
        {
          code: 'LK',
          name: 'Sri Lanka',
        },
        {
          code: 'SD',
          name: 'Sudan',
        },
        {
          code: 'SR',
          name: 'Suriname',
        },
        {
          code: 'SJ',
          name: 'Svalbard and Jan Mayen',
        },
        {
          code: 'SZ',
          name: 'Eswatini',
        },
        {
          code: 'SE',
          name: 'Sweden',
        },
        {
          code: 'CH',
          name: 'Switzerland',
        },
        {
          code: 'SY',
          name: 'Syria',
        },
        {
          code: 'TW',
          name: 'Taiwan',
        },
        {
          code: 'TJ',
          name: 'Tajikistan',
        },
        {
          code: 'TZ',
          name: 'Tanzania',
        },
        {
          code: 'TH',
          name: 'Thailand',
        },
        {
          code: 'TL',
          name: 'Timor-Leste',
        },
        {
          code: 'TG',
          name: 'Togo',
        },
        {
          code: 'TK',
          name: 'Tokelau',
        },
        {
          code: 'TO',
          name: 'Tonga',
        },
        {
          code: 'TT',
          name: 'Trinidad and Tobago',
        },
        {
          code: 'TN',
          name: 'Tunisia',
        },
        {
          code: 'TR',
          name: 'Turkey',
        },
        {
          code: 'TM',
          name: 'Turkmenistan',
        },
        {
          code: 'TC',
          name: 'Turks and Caicos Islands',
        },
        {
          code: 'TV',
          name: 'Tuvalu',
        },
        {
          code: 'UG',
          name: 'Uganda',
        },
        {
          code: 'UA',
          name: 'Ukraine',
        },
        {
          code: 'AE',
          name: 'United Arab Emirates',
        },
        {
          code: 'GB',
          name: 'United Kingdom',
        },
        {
          code: 'US',
          name: 'United States',
        },
        {
          code: 'UM',
          name: 'U.S. Outlying Islands',
        },
        {
          code: 'UY',
          name: 'Uruguay',
        },
        {
          code: 'UZ',
          name: 'Uzbekistan',
        },
        {
          code: 'VU',
          name: 'Vanuatu',
        },
        {
          code: 'VE',
          name: 'Venezuela',
        },
        {
          code: 'VN',
          name: 'Vietnam',
        },
        {
          code: 'VG',
          name: 'British Virgin Islands',
        },
        {
          code: 'VI',
          name: 'U.S. Virgin Islands',
        },
        {
          code: 'WF',
          name: 'Wallis and Futuna',
        },
        {
          code: 'EH',
          name: 'Western Sahara',
        },
        {
          code: 'YE',
          name: 'Yemen',
        },
        {
          code: 'ZM',
          name: 'Zambia',
        },
        {
          code: 'ZW',
          name: 'Zimbabwe',
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      resource: 'icos/getIco',
    }),
  },
  methods: {
    ...mapActions({
      create: 'icos/create',
    }),
    onInputSetDate: function(date, dateType) {
      this.formData[dateType] = formatISO(date)
    },
    parseISO(date) {
      return date ? parseISO(date) : null
    },
    formatISOToCalender(date) {
      return date ? format(parseISO(date), 'yyyy-MM-dd hh:mm:ss', new Date()) : null
    },
    async createResource() {
      return await this.create({
        formData: {
          ...this.formData,
          ico_closing_date: this.formatISOToCalender(this.formData.ico_closing_date),
          ico_opening_date: this.formatISOToCalender(this.formData.ico_opening_date),
          pre_ico_closing_date: this.formatISOToCalender(this.formData.pre_ico_closing_date),
          pre_ico_opening_date: this.formatISOToCalender(this.formData.pre_ico_opening_date),
        },
      })
    },
  },
}
</script>
